import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KbLauncher } from "@nrgpix/kb";
import ConductReportingController from "./ConductReportingController";
import ToggleSection from "../../../common/toggle/ToggleSection";
import HrToolLogos from "../../../corporate_details/hr_sync/HrToolLogos";
import { PILLAR_LABELS, PILLAR_COLORS } from "./blueprintReportingTemplates";

/**
 * 📝 CONDUCT ESG REPORTING
 * The entry point of the reporting flow. Leads the user step by step:
 *   1. Entering data for — the company (must come first)
 *   2. Pillar — Environmental / Social / Governance
 *   3. Topic — the ESRS-based topics on that pillar's page
 *   4. The topic's generated form, saved as a versioned "report section".
 */

// The smart-meter simulation does not belong on this landing page — hidden
// until the meter feed has its proper home. The code paths stay intact.
const SHOW_METER_SIMULATION = false;

function StepLabel({ n, children }) {
  return (
    <h6 className="extra-small fw-bold text-uppercase text-muted mb-2" style={{ letterSpacing: "0.5px" }}>
      <span className="badge rounded-pill bg-dark me-2">{n}</span>
      {children}
    </h6>
  );
}

function FieldControl({ field, value, onChange }) {
  const id = `f_${field.path}`;
  const common = { id, className: "form-control form-control-sm" };

  if (field.type === "boolean") {
    return (
      <div className="form-check form-switch">
        <input
          className="form-check-input"
          type="checkbox"
          id={id}
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
        />
        <label className="form-check-label extra-small" htmlFor={id}>
          {value ? "Yes" : "No"}
        </label>
      </div>
    );
  }

  if (field.type === "enum") {
    return (
      <select
        {...common}
        className="form-select form-select-sm"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— select —</option>
        {(field.options || []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === "richtext") {
    return (
      <textarea
        {...common}
        rows={3}
        value={value || ""}
        placeholder={field.placeholder || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (field.type === "date") {
    return (
      <input
        {...common}
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (field.type === "number") {
    const input = (
      <input
        {...common}
        type="number"
        inputMode="decimal"
        value={value === 0 || value ? value : ""}
        placeholder={field.placeholder || "0"}
        onChange={(e) =>
          onChange(e.target.value === "" ? "" : Number(e.target.value))
        }
      />
    );
    return field.unit ? (
      <div className="input-group input-group-sm">
        {input}
        <span className="input-group-text">{field.unit}</span>
      </div>
    ) : (
      input
    );
  }

  // text
  return (
    <input
      {...common}
      type="text"
      value={value || ""}
      placeholder={field.placeholder || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

const pillarColor = (pillar) => PILLAR_COLORS[pillar] || PILLAR_COLORS.GEN;

function PillarBadge({ pillar }) {
  return (
    <span className="badge" style={{ backgroundColor: pillarColor(pillar).main }}>
      {PILLAR_LABELS[pillar] || pillar}
    </span>
  );
}

/** Charcoal toggle bar with the pillar colour as its accent - always starts closed. */
function Collapsible({ pillar, open, onToggle, title, aside, children }) {
  return (
    <ToggleSection accent={pillarColor(pillar).main} open={open} onToggle={onToggle} title={title} aside={aside}>
      {children}
    </ToggleSection>
  );
}

/** Open/closed state for a set of panels; everything starts closed. */
function useOpenSet() {
  const [open, setOpen] = useState({});
  return {
    isOpen: (k) => !!open[k],
    toggle: (k) => setOpen((o) => ({ ...o, [k]: !o[k] })),
    setAll: (keys, v) => setOpen(v ? Object.fromEntries(keys.map((k) => [k, true])) : {}),
  };
}

function OpenCloseAll({ keys, openSet }) {
  return (
    <div className="btn-group btn-group-sm">
      <button type="button" className="btn btn-outline-secondary" onClick={() => openSet.setAll(keys, true)}>Open all</button>
      <button type="button" className="btn btn-outline-secondary" onClick={() => openSet.setAll(keys, false)}>Close all</button>
    </div>
  );
}

// Where a value came from (report store SOURCE codes).
const SOURCE_LABELS = {
  MANUAL: "Typed",
  BULK_UPLOAD: "Bulk upload",
  LEDGER: "Accounts & Ledger",
  HR: "HR & Workforce",
  API_CONNECTOR: "Connected system",
  METER: "Meter readings",
  VERSION_RESTORE: "Version restored",
};

function StatusBadge({ status, version }) {
  const draft = status === "DRAFT";
  return (
    <span className={`badge ${draft ? "bg-dark" : "bg-light text-dark border"}`}>
      {draft ? `DRAFT${version ? ` · v${version}` : ""}` : "PRE-DRAFT"}
    </span>
  );
}

/** "Typed · Jane · 09/10/2026" or "Accounts & Ledger · codes 6300 · 14 ledger lines". */
function Provenance({ src }) {
  if (!src) return null;
  const parts = [SOURCE_LABELS[src.sourceCode] || src.sourceCode];
  if (src.sourceRef && src.sourceCode !== "MANUAL") parts.push(src.sourceRef.replace(/^(HR & Workforce|Accounts & Ledger) · /, ""));
  if (src.enteredBy) parts.push(src.enteredBy);
  if (src.enteredAt) parts.push(new Date(src.enteredAt).toLocaleDateString());
  if (!src.enteredBy) parts.push("not saved yet");
  return <div className="extra-small text-muted mt-1" title={src.sourceRef || ""}>{parts.join(" · ")}</div>;
}

const isFilled = (v) => !(v === "" || v === null || v === undefined || v === false);

export default function ConductReportingView() {
  const navigate = useNavigate();
  const c = ConductReportingController();
  const topicPanels = useOpenSet();
  const groupPanels = useOpenSet();

  // Knowledge-base context follows the user through the flow.
  const kbContext =
    c.phase === "form" && c.topic
      ? { title: `${PILLAR_LABELS[c.topic.pillar]} › ${c.topic.name}`, query: `${c.topic.name} ${c.topic.blueprintRefs[0] || ""}`.trim() }
      : c.pillarInfo
        ? { title: c.pillarInfo.label, query: c.pillarInfo.label }
        : { title: "Conduct ESG Reporting", query: "" };

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <div className="row mb-2">
              <div className="col">
                <h2 className="h4 fw-bold mb-1">Conduct ESG Reporting</h2>
                <p className="text-muted small mb-0">
                  Choose the company, then the pillar, then the topic you are
                  reporting on. Each topic is a form you can save as a draft and
                  come back to.
                </p>
              </div>
            </div>

            {c.error && (
              <div className="alert alert-danger extra-small">{c.error}</div>
            )}
            {c.notice && (
              <div className="alert alert-info extra-small">{c.notice}</div>
            )}

            {/* STEP 1 — company selector (this page is standalone) */}
            {c.phase === "pick" && <StepLabel n={1}>Entering data for</StepLabel>}
            <div className="card border-0 shadow-sm bg-light mb-3">
              <div className="card-body py-3 d-flex flex-wrap align-items-center gap-3">
                <label
                  className="extra-small fw-bold text-uppercase text-muted mb-0"
                  htmlFor="cr-company"
                  style={{ letterSpacing: "0.5px" }}
                >
                  Entering data for
                </label>
                {c.loadingCompanies ? (
                  <span className="small text-muted">
                    <span className="spinner-border spinner-border-sm me-2" />
                    Loading your companies…
                  </span>
                ) : c.companies.length > 0 ? (
                  <>
                    <select
                      id="cr-company"
                      className="form-select form-select-sm"
                      style={{ width: "auto", minWidth: 260 }}
                      value={c.activeCompanyId != null ? String(c.activeCompanyId) : ""}
                      onChange={(e) => c.pickCompany(e.target.value)}
                    >
                      <option value="">Select a company…</option>
                      {c.companies.map((co) => (
                        <option key={co.id} value={co.id}>
                          {co.name}
                        </option>
                      ))}
                    </select>
                    {c.activeCompanyName && (
                      <span className="badge bg-dark">{c.activeCompanyName}</span>
                    )}
                  </>
                ) : (
                  <span className="small text-muted">
                    You are not assigned to any company yet.
                  </span>
                )}
              </div>
            </div>

            {!c.hasCompany && (
              <div className="alert alert-warning extra-small">
                Choose the company you are entering data for — everything below
                belongs to it.
              </div>
            )}

            {/* ================= STEP 2 — PILLAR ================= */}
            {c.phase === "pick" && c.hasCompany && !c.pillar && (
              <>
                <StepLabel n={2}>Which pillar do you want to work on?</StepLabel>
                <div className="row g-3 mb-4">
                  {c.pillars.map((p) => (
                    <div key={p.key} className="col-md-4">
                      <button
                        type="button"
                        className="card h-100 w-100 text-start shadow-sm border dashboard-card p-0"
                        style={{ borderTop: `5px solid ${pillarColor(p.key).main}`, backgroundColor: pillarColor(p.key).soft }}
                        onClick={() => { topicPanels.setAll([], false); c.pickPillar(p.key); }}
                      >
                        <div className="card-body d-flex flex-column">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <i className={`bi ${p.icon} fs-4`} style={{ color: pillarColor(p.key).main }} />
                            <PillarBadge pillar={p.key} />
                          </div>
                          <h5 className="fw-bold mb-1">{p.label}</h5>
                          <p className="text-muted extra-small mb-2 flex-grow-1">{p.blurb}</p>
                          <div className="extra-small text-muted">{p.topicCount} topics</div>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ================= STEP 3 — TOPIC ================= */}
            {c.phase === "pick" && c.hasCompany && c.pillar && (
              <>
                {SHOW_METER_SIMULATION && (
                <div className="card shadow-sm border-0 bg-white p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    <div>
                      <h6 className="extra-small fw-bold text-uppercase text-primary mb-1">
                        Smart-meter simulation
                      </h6>
                      <p className="text-muted extra-small mb-0" style={{ maxWidth: 620 }}>
                        Stands in for a live meter feed. Generates a month of
                        daily register readings (electricity, gas, district heat,
                        water) for{" "}
                        <strong>{c.activeCompanyName || "the active company"}</strong>
                        . The Energy and Water forms can then pull period totals
                        straight from it.
                      </p>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-primary fw-bold flex-shrink-0"
                      disabled={!c.hasCompany || c.simBusy}
                      onClick={c.simulateMeters}
                    >
                      {c.simBusy ? "Simulating…" : "Simulate meter data"}
                    </button>
                  </div>
                  {c.simBatch && (
                    <div className="extra-small text-muted mt-2 border-top pt-2">
                      Last batch: <strong>{c.simBatch.readingCount}</strong>{" "}
                      readings from{" "}
                      <strong>{c.simBatch.meterPointCount}</strong> meters,{" "}
                      {c.simBatch.periodStart} → {c.simBatch.periodEnd}{" "}
                      (generated {new Date(c.simBatch.generatedAt).toLocaleString()})
                    </div>
                  )}
                </div>
                )}

                <div className="d-flex justify-content-between align-items-center mb-2">
                  <StepLabel n={3}>
                    {c.pillarInfo?.label} — which topic are you reporting on?
                  </StepLabel>
                  <button className="btn btn-sm btn-outline-secondary mb-2" onClick={c.backToPillars}>
                    ← Choose another pillar
                  </button>
                </div>
                {c.pillar === "S" && (
                  <HrToolLogos title="People figures can come straight from your HR system" compact />
                )}
                <div className="d-flex justify-content-end mb-2">
                  <OpenCloseAll keys={c.templates.map((t) => t.key)} openSet={topicPanels} />
                </div>
                <div className="mb-4">
                  {c.templates.map((t) => (
                    <Collapsible
                      key={t.key}
                      pillar={t.pillar}
                      open={topicPanels.isOpen(t.key)}
                      onToggle={() => topicPanels.toggle(t.key)}
                      title={
                        <>
                          <span className="fw-bold">{t.name}</span>
                          <span className="extra-small ms-2" style={{ color: "rgba(255,255,255,0.7)" }}>{t.topic}</span>
                        </>
                      }
                      aside={<span className="extra-small text-nowrap">{t.blueprintRefs.join(" · ")}</span>}
                    >
                      <p className="small mb-2">{t.summary}</p>
                      {t.guidance && <p className="text-muted extra-small mb-2">{t.guidance}</p>}
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        {t.groups.map((g) => (
                          <span key={g.key} className="badge border text-dark fw-normal" style={{ backgroundColor: pillarColor(t.pillar).soft }}>
                            {g.label} · {g.fieldCount}
                          </span>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm fw-bold text-white"
                        style={{ backgroundColor: pillarColor(t.pillar).main }}
                        onClick={() => { groupPanels.setAll([], false); c.startTopic(t.key); }}
                      >
                        Enter data for {t.name} →
                      </button>
                      <KbLauncher
                        className="ms-2"
                        label="Guidance"
                        context={{ title: `${PILLAR_LABELS[t.pillar]} › ${t.name}`, query: `${t.name} ${t.blueprintRefs[0] || ""}`.trim() }}
                      />
                    </Collapsible>
                  ))}
                </div>

                {/* saved sections */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="extra-small fw-bold text-uppercase text-muted mb-0">
                    Saved {c.pillarInfo?.label} sections
                  </h6>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={c.refreshList}
                    disabled={c.loadingList}
                  >
                    Refresh
                  </button>
                </div>
                {c.listNotice && (
                  <div className="alert alert-warning extra-small">
                    {c.listNotice}
                  </div>
                )}
                {c.loadingList ? (
                  <div className="text-center py-4">
                    <span className="spinner-border spinner-border-sm text-primary" />
                  </div>
                ) : c.submissions.length === 0 ? (
                  <p className="text-muted extra-small fst-italic">
                    Nothing saved yet for this company.
                  </p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr className="extra-small text-uppercase text-muted">
                          <th>Section</th>
                          <th>Values</th>
                          <th>Period</th>
                          <th>Status</th>
                          <th>Ver.</th>
                          <th>Updated</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {c.submissions.map((s) => (
                          <tr key={s.id}>
                            <td className="small fw-bold">{s.topicName}</td>
                            <td className="extra-small text-muted">{s.valueCount ?? "—"}</td>
                            <td className="extra-small text-muted">
                              {s.periodStart && s.periodEnd
                                ? `${s.periodStart} → ${s.periodEnd}`
                                : "—"}
                            </td>
                            <td>
                              <StatusBadge status={s.status} />
                            </td>
                            <td className="extra-small">{s.version ? `v${s.version}` : "—"}</td>
                            <td className="extra-small text-muted">
                              {s.updatedAt
                                ? new Date(s.updatedAt).toLocaleDateString()
                                : "—"}
                              {s.updatedBy ? ` · ${s.updatedBy}` : ""}
                            </td>
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => c.openSubmission(s)}
                              >
                                Open
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* ================= FORM PHASE ================= */}
            {c.phase === "form" && c.topic && (
              <>
                <div className="card shadow-sm border-0 bg-light p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <PillarBadge pillar={c.topic.pillar} />
                        <h5 className="fw-bold mb-0">{c.topic.name}</h5>
                        <StatusBadge status={c.status} version={c.currentVersionNo} />
                      </div>
                      <p className="text-muted extra-small mb-1">
                        {c.topic.guidance}
                      </p>
                      <span className="extra-small text-muted">
                        {c.topic.blueprintRefs.join(" · ")} ·{" "}
                        {c.editingId ? "stored section" : "not saved yet"}
                        {c.draftedBy ? ` · set to Draft by ${c.draftedBy.by} on ${new Date(c.draftedBy.at).toLocaleDateString()}` : ""}
                      </span>
                    </div>
                    <div className="d-flex flex-column gap-2 flex-shrink-0">
                      <KbLauncher label="Guidance" context={kbContext} />
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={c.backToPick}
                      >
                        ← {c.topic.pillar === "E" ? "Environmental" : c.topic.pillar === "S" ? "Social" : "Governance"} topics
                      </button>
                      {SHOW_METER_SIMULATION && c.canPullMeters && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={c.pullFromMeters}
                        >
                          ⭳ Pull from smart meters
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {c.topic.pillar === "E" && (
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    <button type="button" className="btn btn-sm text-white fw-bold" style={{ backgroundColor: pillarColor("E").main }}
                      disabled={c.ledgerBusy} onClick={c.fillFromLedger}>
                      {c.ledgerBusy ? "Reading ledger figures…" : "Fill from Accounts & Ledger"}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => navigate("/registered/company-accounts")}>
                      Manage account codes &amp; figures
                    </button>
                  </div>
                )}
                {c.topic.hr && (
                  <>
                    <HrToolLogos compact />
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                      <button type="button" className="btn btn-sm text-white fw-bold" style={{ backgroundColor: pillarColor("S").main }}
                        disabled={c.hrBusy} onClick={c.fillFromHr}>
                        {c.hrBusy ? "Reading HR figures…" : "Fill from HR & Workforce"}
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => navigate("/registered/company-hr")}>
                        Manage HR data
                      </button>
                    </div>
                  </>
                )}
                <div className="d-flex justify-content-end mb-2">
                  <OpenCloseAll keys={c.topic.groups.map((g) => g.key)} openSet={groupPanels} />
                </div>
                {c.topic.groups.map((g) => {
                  const filled = g.fields.filter((f) => isFilled(c.values[`${g.key}.${f.key}`])).length;
                  const missingReq = g.fields.some(
                    (f) => f.required && f.type !== "boolean" && !isFilled(c.values[`${g.key}.${f.key}`]),
                  );
                  return (
                  <Collapsible
                    key={g.key}
                    pillar={c.topic.pillar}
                    open={groupPanels.isOpen(g.key)}
                    onToggle={() => groupPanels.toggle(g.key)}
                    title={<span className="fw-bold small">{g.label}</span>}
                    aside={
                      <span className="extra-small text-nowrap">
                        {missingReq && <span className="me-2" style={{ color: "#ff8a8a" }}>required missing</span>}
                        <span>{filled}/{g.fields.length} filled</span>
                      </span>
                    }
                  >
                    {g.help && (
                      <p className="text-muted extra-small mb-3">{g.help}</p>
                    )}
                    <div className="row g-3">
                      {g.fields.map((f) => {
                        const path = `${g.key}.${f.key}`;
                        return (
                          <div key={path} className="col-md-6 col-lg-4">
                            <label
                              className="extra-small fw-bold text-muted mb-1 d-block"
                              htmlFor={`f_${path}`}
                            >
                              {f.label}
                              {f.required && (
                                <span className="text-danger"> *</span>
                              )}
                            </label>
                            <FieldControl
                              field={{ ...f, path }}
                              value={c.values[path]}
                              onChange={(v) => c.setValue(path, v)}
                            />
                            <Provenance src={c.sources[path]} />
                            {f.guidance && (
                              <div className="form-text extra-small">
                                {f.guidance}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Collapsible>
                  );
                })}

                {c.missing.length > 0 && (
                  <div className="alert alert-warning extra-small">
                    <strong>{c.missing.length}</strong> required field(s) still
                    empty — you can keep saving; fill them before setting the section to Draft:
                    <ul className="mb-0 mt-1">
                      {c.missing.slice(0, 8).map((m) => (
                        <li key={m}>{m}</li>
                      ))}
                      {c.missing.length > 8 && <li>…and {c.missing.length - 8} more</li>}
                    </ul>
                  </div>
                )}

                {c.status === "DRAFT" && (
                  <div className="border rounded p-3 mb-2" style={{ borderColor: pillarColor(c.topic.pillar).main }}>
                    <div className="small fw-bold mb-2">
                      This section is a Draft — every change needs a reason and becomes a new version.
                    </div>
                    <div className="row g-2">
                      <div className="col-md-5">
                        <select className="form-select form-select-sm" value={c.reason.code}
                          onChange={(e) => c.setReason((r) => ({ ...r, code: e.target.value }))}>
                          <option value="">— reason for the change —</option>
                          {c.reasonCodes.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                        </select>
                      </div>
                      <div className="col-md-7">
                        <input className="form-control form-control-sm" placeholder={c.reason.code === "OTHER" ? "Explain the change (required)" : "Note (optional)"}
                          value={c.reason.note} onChange={(e) => c.setReason((r) => ({ ...r, note: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 border-top pt-3 mt-2">
                  <button
                    className="btn btn-link text-decoration-none p-0 text-secondary small"
                    onClick={c.backToPick}
                  >
                    ← Back without saving
                  </button>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm fw-bold text-white"
                      style={{ backgroundColor: pillarColor(c.topic.pillar).main }}
                      disabled={c.saving || !c.hasCompany || (c.status === "DRAFT" && !c.reason.code)}
                      onClick={c.save}
                    >
                      {c.saving ? "Saving…" : c.status === "DRAFT" ? "Save as new version" : "Save"}
                    </button>
                    {c.status !== "DRAFT" && (
                      <button
                        className="btn btn-sm btn-dark fw-bold"
                        disabled={c.saving || !c.editingId}
                        title={c.editingId ? "" : "Save the section first"}
                        onClick={c.setDraft}
                      >
                        Set to Draft
                      </button>
                    )}
                  </div>
                </div>

                {c.status === "DRAFT" && c.versions.length > 0 && (
                  <ToggleSection className="mt-3 mb-2" accent={pillarColor(c.topic.pillar).main} title="Versions"
                    aside={`current v${c.currentVersionNo} of ${c.versions.length}`}>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr className="extra-small text-uppercase text-muted"><th>Version</th><th>Reason</th><th>Note</th><th>By</th><th>When</th><th /></tr>
                        </thead>
                        <tbody>
                          {c.versions.map((v) => (
                            <tr key={v.versionNo}>
                              <td className="small fw-bold">v{v.versionNo}{v.isCurrent && <span className="badge bg-dark ms-2">current</span>}</td>
                              <td className="small">{v.reasonLabel || v.reasonCode}</td>
                              <td className="small text-muted">{v.reasonNote || "—"}</td>
                              <td className="small">{v.createdBy || "—"}</td>
                              <td className="small text-muted">{v.createdAt ? new Date(v.createdAt).toLocaleString() : ""}</td>
                              <td className="text-end">
                                {!v.isCurrent && (
                                  <button className="btn btn-sm btn-outline-dark" disabled={c.saving} onClick={() => c.makeCurrent(v.versionNo)}>
                                    Make current
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </ToggleSection>
                )}

                {c.history.length > 0 && (
                  <ToggleSection className="mb-2" accent={pillarColor(c.topic.pillar).main} title="Change history"
                    aside={`${c.history.length} change${c.history.length === 1 ? "" : "s"}`}>
                    <div className="table-responsive" style={{ maxHeight: 360, overflowY: "auto" }}>
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr className="extra-small text-uppercase text-muted"><th>When</th><th>Field</th><th>From</th><th>To</th><th>Source</th><th>Reason</th><th>By</th></tr>
                        </thead>
                        <tbody>
                          {c.history.map((h, i) => (
                            <tr key={i}>
                              <td className="extra-small text-muted text-nowrap">{new Date(h.changedAt).toLocaleString()}</td>
                              <td className="extra-small">{h.fieldPath}</td>
                              <td className="extra-small text-muted">{h.oldValue ?? "—"}</td>
                              <td className="extra-small fw-bold">{h.newValue ?? "—"}</td>
                              <td className="extra-small" title={h.sourceRef || ""}>{h.sourceLabel || h.sourceCode}</td>
                              <td className="extra-small">
                                {h.reasonLabel || (h.statusAtChange === "PRE_DRAFT" ? "pre-draft" : "—")}
                                {h.versionNo ? ` · v${h.versionNo}` : ""}
                                {h.reasonNote ? ` · ${h.reasonNote}` : ""}
                              </td>
                              <td className="extra-small">{h.changedBy || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </ToggleSection>
                )}
              </>
            )}

            <KbLauncher variant="floating" context={kbContext} />

            {/* FOOTER NAV */}
            <div className="mt-5 pt-4 border-top">
              <button
                className="btn btn-link text-decoration-none p-0 text-secondary small"
                onClick={() => navigate("/registered/dashboard")}
              >
                <i className="bi bi-arrow-left me-2"></i> Return to Dashboard
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
